import styled, {css} from 'styled-components';

import {Switch as BaseSwitch} from '@edna/components';
import {mixins} from '@edna/components/styles';
import BaseMoreHorizIcon from '@edna/icons/moreHoriz.svg';
import {hexToRgba} from '@edna/utils';

import {Tabs as BaseTabs} from 'src/components';
import {LogoImage} from 'src/images';

const FONT_SIZE = '12px';
const LINE_HEIGHT = 1.6;

export const truncate = (line: number, fontSize?: string) => css`
  display: block;
  /* stylelint-disable-next-line value-no-vendor-prefix */
  display: -webkit-box;
  max-height: calc(${fontSize ?? FONT_SIZE} * ${LINE_HEIGHT} * ${line});
  /* stylelint-disable-next-line property-no-vendor-prefix */
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${line};
  overflow: hidden;
  text-overflow: ellipsis;
`;

type TDeviceProps = {
  isIOS?: boolean;
};

type TMessageProps = TDeviceProps & {
  collapsed: boolean;
};

export const Message = styled.div<TMessageProps>`
  font-size: ${FONT_SIZE};
  line-height: ${LINE_HEIGHT};
  ${({isIOS, theme: {spacing, palette, branding}}) => css`
    padding: ${spacing.s4} 0;
    background-color: ${branding.backgrounds.primary};
    border-radius: 14px;
    color: ${isIOS ? hexToRgba(palette.black, '60%') : 'currentColor'};
  `}
`;

export const Header = styled.div`
  ${mixins.flexAlign('space-between', 'center')}
  ${({theme: {spacing}}) => css`
    margin: 0 ${spacing.s4} ${spacing.s2} ${spacing.s4};
  `}
`;

export const AppLogo = styled(LogoImage)`
  ${mixins.wh('20px')}
  flex-shrink: 0;
  margin-right: 6px;
`;

export const AppTitle = styled.div`
  ${mixins.flexAlign('normal', 'center')}
`;

export const AppTitleText = styled.div`
  ${truncate(1)}
`;

export const BigTitle = styled.div<TDeviceProps>`
  ${truncate(1)};
  ${({isIOS, theme: {palette}}) => css`
    color: ${palette.black};
    font-weight: ${isIOS ? 700 : 500};
  `}
`;

export const Title = styled(BigTitle)`
  ${truncate(1)}
`;

type TBigBodyProps = TDeviceProps & {
  hasImage?: boolean;
};

export const BigBody = styled.div<TBigBodyProps>`
  ${({isIOS, hasImage, theme}) => css`
    margin-top: ${hasImage ? theme.spacing.s2 : 0};
    margin-bottom: ${isIOS || !hasImage ? 0 : theme.spacing.s2};
    ${truncate(!isIOS && hasImage ? 1 : 6)}
    word-break: break-word;
    white-space: pre-wrap;
  `}
`;

type TBodyProps = TDeviceProps & {
  hasTitle?: boolean;
};

export const Body = styled.div<TBodyProps>`
  ${({isIOS, hasTitle, theme}) => css`
    margin-top: ${theme.spacing.s2};
    word-break: break-word;
    white-space: pre-wrap;
    ${isIOS
      ? truncate(hasTitle ? 3 : 2)
      : css`
          ${truncate(1)}
          max-width: 100%;
        `}
  `}
`;

export const MoreHorizIcon = styled(BaseMoreHorizIcon)`
  color: #909090;
  height: 20px;
`;

export const Time = styled.div`
  color: #838383;
`;

export const Container = styled.div`
  display: flow-root;
  ${({theme: {spacing}}) => css`
    margin: 0 ${spacing.s4};
  `}
`;

export const BigLogoWrapper = styled.div<TDeviceProps>`
  ${({isIOS, theme: {spacing}}) =>
    !isIOS &&
    css`
      overflow: hidden;
      margin: ${spacing.s1} ${spacing.s4} 0 ${spacing.s4};
    `}
`;

export const BigLogo = styled.img<TDeviceProps>`
  position: relative;
  width: 100%;
  max-height: 500px;
  ${({isIOS}: TDeviceProps) =>
    !isIOS &&
    css`
      width: 110%;
      margin: -5%;
    `}
`;

export const Logo = styled.img<TDeviceProps>`
  ${mixins.wh('36px')}
  flex-shrink: 0;
  float: right;
  ${({isIOS}: TDeviceProps) =>
    isIOS &&
    css`
      border-radius: 4px;
    `}
`;

export const Action = styled.div<TDeviceProps>`
  ${mixins.truncate}
  ${({isIOS, theme: {spacing}}) =>
    isIOS
      ? css`
          min-height: 40px;
          ${mixins.flexAlign('center')}
          &:not(:last-child) {
            border-bottom: 1px solid ${hexToRgba('#999999', '50%')};
          }
        `
      : css`
          color: #757575;
          &:not(:last-child) {
            margin-right: ${spacing.s5};
          }
        `}
`;

export const Actions = styled.div<TDeviceProps>`
  ${({isIOS, theme: {spacing, branding}}) => css`
    margin-top: ${spacing.s2};
    ${isIOS
      ? css`
          width: 100%;
          background-color: ${branding.backgrounds.primary};
          border-radius: 13px;
        `
      : css`
          display: flex;
          color: #757575;
          margin-left: ${spacing.s4};
        `}
  `}
`;

export const PhonePreview = styled.div`
  position: relative;
  ${mixins.flexAlign('center')}
  flex-direction: column;
  width: 100%;
  min-height: 670px;
  border-radius: ${({theme}) => theme.borderRadius.br1};
`;

export const Tabs = styled(BaseTabs)`
  max-width: max-content;
`;

export const Device = styled.div`
  display: flex;
  align-items: center;
  max-width: max-content;
  min-height: 30px;

  ${({theme}) => css`
    padding: 0 ${theme.spacing.s2};
    border: ${theme.dimensions.borderWidth}px solid ${theme.branding.content.tertiary};
    border-radius: ${theme.borderRadius.br8};
  `}
`;

export const DeviceIcon = styled.div`
  ${mixins.wh('20px')}
  flex-shrink: 0;
  margin-right: ${({theme}) => theme.spacing.s1};
`;

export const PreviewBackground = styled.svg`
  ${mixins.wh('100%')}
  ${mixins.position({position: 'absolute', top: '0', left: '0'})}
  border-radius: 39px;
  color: ${({theme}) => theme.branding.content.secondary};
`;

type TContentProps = {
  isIOS?: boolean;
};

export const Content = styled.div<TContentProps>`
  width: calc(100% - 23px);
  ${mixins.position({position: 'absolute', top: '0', left: '0'})}
  margin: 39px 0 0 11px;
  border-radius: 0 0 35px 35px;
  overflow: hidden;
  ${({isIOS}: TContentProps) => css`
    height: calc(100% - ${isIOS ? 51 : 59}px);
    border-radius: ${isIOS ? '0 0 40px 37px' : '30px 33px 34px 32px'};
  `}
`;

export const MainBody = styled.div`
  height: 100%;
  overflow: hidden auto;
  padding: ${({theme: {spacing}}) => `${spacing.s12} ${spacing.s3} ${spacing.s12} ${spacing.s2}`};

  &::-webkit-scrollbar {
    ${mixins.wh('4px')}
  }
`;

export const Switch = styled(BaseSwitch)`
  max-width: max-content;
  color: ${({theme}) => theme.branding.content.primary};
`;

export const SwitchContent = styled.div`
  ${mixins.f1}
`;
